import { createTheme, Grid, IconButton, MuiThemeProvider, Typography } from '@material-ui/core';
import { Add as AddIcon, Delete as DeleteIcon } from '@material-ui/icons';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import React, { useRef } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import SelectRackType from '../../../components/Form/SelectRackType';
import TextField from '../../../components/Form/TextField';
import AlertDialog from '../../../components/ui/AlertDialog';
import ButtonUI from '../../../components/ui/ButtonUI';
import { useAuth } from '../../../hooks/auth';
import getDefaultGridOptions, { defaultHeaderOptions } from '../../../utils/componentUtils';
import { useStyles } from '../Style';

type props =  {
    control: any,
    name: string,
    isView: boolean,
};

interface IFormInput {
    rackType: any;
    rackTypeQuantity: any;
}

//allowInvoiceEdit: boolean - Indica se deve exibir a coluna para editar NF de saida
//showColumns: array - define quais colunas adicionais devem ser exibidas na grid.
const MvmOutgoingInvoiceRequestRackTypeList: React.FC<props> = ({ control, name, isView }) => {
    useAuth()
    const classes = useStyles()
    const { t: tMvm } = useTranslation("Mvm")
    const { t: _t } = useTranslation("General")
    const { t: tval } = useTranslation("Validation")
    const { t: tMvmRackLoad } = useTranslation("MvmRackLoad")
    const { fields: _fields, append, remove } = useFieldArray({ control, name: name });
    const fields = _fields as Record<string, any>[];
    const { control: formControl, setError, clearErrors, reset, getValues } = useForm<IFormInput>({
        defaultValues: {
            rackType: {},
            rackTypeQuantity: '',
        }
    });
    const refRackTypeField = useRef<HTMLElement>(null);
    const { enqueueSnackbar } = useSnackbar();

    const myTheme = createTheme({
        overrides: {
            MuiTableBody: {
                root: {
                    cursor: ''
                }
            }
        }
    })

   
    const columns = [
        {
            name: 'rackTypeDescription',
            label: String(tMvmRackLoad('rackTypeDescription')),
            options: {
                ...defaultHeaderOptions,
                customBodyRender: (value: any, tableMeta: any) => (
                        fields[tableMeta.rowIndex]["rackTypeCode"] + ' - ' + value
                    )
            }
        },
        {
            name: 'rackTypeQuantity',
            label: String(tMvmRackLoad('rackTypeQuantity')),
            options: {
                ...defaultHeaderOptions,
                sort: false
            }
        },
        {
            name: '',
            label: '',
            options: {
                setCellProps: () => ({
                    align: "right"
                }),
                disableClick: true,
                sort: false,
                customBodyRender: (value: any, tableMeta: any) => {
                    return (isView ? <></> : renderButtons(value, tableMeta.rowIndex))
                }
            }
        },
    ]

    const yupValidation = async (data: any) => {
        clearErrors([ "rackType", "rackTypeQuantity"]);

        try {
            const schema = Yup.object({
                rackType: Yup.object().nullable()
                    .required(tval("requiredField"))
                    .shape({code: Yup.string().required(tval("requiredField"))}),
                rackTypeQuantity: Yup.number()
                    .required(tval("requiredField"))
                    .positive(tval("mustBeGreaterThan0"))
                    .typeError(tval("mustBeNumber")),
                
            })

            await schema.validate(data, {
                abortEarly: false,
            })

        } catch (err) {
            
            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {
                    setError(error.path?.split('.')[0] as "rackType" | "rackTypeQuantity", { message: error.message })
                })
            }
            
            return false;
        }

        return true;
    }

  
    const renderButtons = (id: any, rowIndex: any) => {
        
        if (isView)
            return (<></>)

        return (
            <AlertDialog
                dialogTitle={_t("dialogDeleteTitle")}
                dialogContentText={_t("dialogDeleteContentText")}
                cancelTextButton={_t("cancelDeleteTextButton")}
                confirmTextButton={_t("confirmDeleteTextButton")}
                confirmDialogFunction={() => { removeItemFromList(rowIndex) }}
                Button={
                    <IconButton size='small' edge="end">
                        <DeleteIcon />
                    </IconButton>
                }
            />
        )
    }

    const addItemToList = async (data: any) => {
        var validForm = await yupValidation(data)

        if (!validForm)
            return

        var existingItem = false;
        fields.forEach((field: any) => {

            if (field.rackTypeId === data.rackType.rackTypeId){
                existingItem = true;
            }
        })

        if (existingItem) {
            enqueueSnackbar(tMvmRackLoad("itemAlreadyOnList"), { variant: 'warning' })
            refRackTypeField.current?.focus();
            return;
        }

        append({
            rackTypeId: data.rackType.rackTypeId,
            rackTypeCode: data.rackType.rackTypeCode,
            rackTypeDescription: data.rackType.rackTypeDescription,
            rackTypeQuantity: data.rackTypeQuantity})
        
        reset()    
        refRackTypeField.current?.focus()
    }

   
    const removeItemFromList = (index: number) => {
        remove(index)
    }
    
    return (
        <Grid style={{ marginTop: 20, width: "98%", marginLeft: 10, marginBottom: 10 }}>
            <Typography className={classes.root} style={{ marginLeft: 0, marginBottom: 10 }} >
                {tMvm("outgoingRequests")}
            </Typography>

            <form >
            
            {isView ? <></> :
                <Grid container spacing={1} style={{  marginLeft: -12, marginBottom: 10 }}>

                    <Grid item xs={2} >
                        <SelectRackType
                            label={tMvmRackLoad('rackType')}
                            name="rackType"
                            control={formControl}
                            inputRef={refRackTypeField} 
                            //style={{width: '100%'}}
                            multiple={false} />
                    </Grid>
                        
                    <Grid item xs={1} >
                        <TextField
                            label={tMvmRackLoad("rackTypeQuantity")}
                            name="rackTypeQuantity"
                            control={formControl}
                            type="number"
                            fullWidth />
                    </Grid>
                    
                    <Grid item xs={1}>
                        <ButtonUI
                            onClick={e => addItemToList(getValues())}
                            color="primary"
                            variant="contained"
                            style={{ maxWidth: '40px', maxHeight: '38px', minWidth: '40px', minHeight: '38px', marginTop: 18, marginLeft: '10px' }}
                            >
                            <AddIcon />
                        </ButtonUI>
                    </Grid>
                </Grid>
            }
            </form>

            <Typography className={classes.paper} style={{ marginLeft: 0 }} >
                {_t("totalRecords")} {fields ? fields.length : '0'}
            </Typography>
            <MuiThemeProvider theme={myTheme} >
                <MUIDataTable
                    title=""
                    data={fields}
                    columns={columns}
                    options={{
                        ...getDefaultGridOptions(_t),
                        serverSide: false,
                        pagination: false,
                        sort: false,
                        selectToolbarPlacement: "none",
                        filter: false,
                        search: false,
                        download: false,
                        print: false,
                        viewColumns: false,
                        selectableRows: 'none',
                    }}
                />
            </MuiThemeProvider>

        </Grid>
    )
}

export default MvmOutgoingInvoiceRequestRackTypeList